import { faChevronDown, faChevronUp, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MermaidDiagram from "../components/Mermaid";
import { zenStyles } from "../components/zen";
import { UserAuth } from "../context/AuthContext";
import { CACHE_KEYS, CACHE_TTL, getFromCache, setInCache } from "../utils/cacheUtils";
import {
  ALL_COUNTRIES,
  ALL_NICE_CLASSES,
  POPULAR_COUNTRIES,
  POPULAR_CLASSES,
  getGradeColor,
} from "../utils/trademarkData";

// Step definitions for trademark search journey
const STEPS = [
  {
    key: "name",
    prompt: "What brand name do you want to search?",
    placeholder: "enter brand name...",
    showHistory: true,
  },
  { key: "countries", prompt: "Where do you need protection?", tiles: "countries" },
  { key: "classes", prompt: "Which industry classes?", tiles: "classes" },
  { key: "review", prompt: "Ready to search?", review: true },
];

function Scrapers() {
  const { user } = UserAuth();
  const host = process.env.REACT_APP_SERVER;
  const navigate = useNavigate();

  // Journey state
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    countries: [],
    classes: [],
    searchType: "contains",
  });

  // Search and expand states for tile grids
  const [countrySearch, setCountrySearch] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [showAllClasses, setShowAllClasses] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);

  // Results state
  const [showResults, setShowResults] = useState(false);
  const [data, setData] = useState({});

  // Ref for focusing input
  const inputRef = useRef(null);

  // Load history from localStorage on mount
  useEffect(() => {
    const cached = getFromCache(CACHE_KEYS.trademarkHistory(), CACHE_TTL.TRADEMARK_HISTORY);
    if (cached && Array.isArray(cached)) {
      setHistory(cached);
    }
  }, []);

  // Focus input when step changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const canProceed = () => {
    if (currentStep.key === "name") return formData.name?.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (isLastStep) {
      handleSearch();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canProceed()) handleNext();
    }
  };

  const handleInputChange = value => {
    setFormData({ ...formData, [currentStep.key]: value });
  };

  // Save search to history (including results)
  const saveToHistory = (searchData, resultsData) => {
    const newEntry = {
      id: Date.now(),
      query: searchData.name,
      countries: searchData.countries,
      classes: searchData.classes,
      grade: resultsData.grade,
      resultsCount: resultsData.sample_results?.length || 0,
      searchedAt: new Date().toISOString(),
      // Save the full results for instant replay
      results: resultsData,
    };

    // Dedupe by query+countries+classes
    const key = `${newEntry.query}-${(newEntry.countries || []).sort().join(",")}-${(newEntry.classes || []).sort().join(",")}`;
    const filteredHistory = history.filter(h => {
      const hKey = `${h.query}-${(h.countries || []).sort().join(",")}-${(h.classes || []).sort().join(",")}`;
      return hKey !== key;
    });

    // Keep last 10
    const updatedHistory = [newEntry, ...filteredHistory].slice(0, 10);
    setHistory(updatedHistory);
    setInCache(CACHE_KEYS.trademarkHistory(), updatedHistory);
  };

  // Load from history item - use cached results instantly
  const loadFromHistory = item => {
    const searchData = {
      name: item.query,
      countries: item.countries || [],
      classes: item.classes || [],
      searchType: "contains",
    };
    setFormData(searchData);

    // Use cached results if available
    if (item.results) {
      setData(item.results);
      setShowResults(true);
    } else {
      // Fallback: re-run search if no cached results (old history format)
      runSearch(searchData);
    }
  };

  // Shared search logic
  const runSearch = async searchData => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("q", searchData.name);

      if (searchData.countries.length > 0) {
        params.append("countries", searchData.countries.join(","));
      }
      if (searchData.classes.length > 0) {
        params.append("nice_classes", searchData.classes.join(","));
      }
      if (searchData.searchType) {
        params.append("search_type", searchData.searchType);
      }

      const response = await axios.get(`${host}/api/tmview`, {
        params: Object.fromEntries(params),
        timeout: 100000,
      });

      if (response.status === 200) {
        setData(response.data);
        saveToHistory(searchData, response.data);
        setShowResults(true);
      } else {
        setError("Failed to fetch trademark data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch trademark data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Search handler
  const handleSearch = () => {
    if (!formData.name.trim()) return;
    runSearch(formData);
  };

  // New search handler
  const handleNewSearch = () => {
    setShowResults(false);
    setData({});
    setFormData({
      name: "",
      countries: [],
      classes: [],
      searchType: "contains",
    });
    setStep(0);
    setError(null);
  };

  if (!user?.email) return null;

  // Full-screen results view
  if (showResults && data && Object.keys(data).length > 0) {
    const adaptedData = {
      sample_results: data.sample_results || data.conflicts || [],
      grade: data.grade || "N/A",
      count: data.count || 0,
      url: data.url || data.tmview_link || null,
    };

    const exactMatches =
      adaptedData.sample_results?.filter(r => r.similarity_to_query === 1.0 || r.similarity_to_query === 1) || [];
    const highSimilarityMatches =
      adaptedData.sample_results?.filter(r => r.similarity_to_query >= 0.7 && r.similarity_to_query < 1.0) || [];

    const getSimilarityColor = sim => {
      if (sim >= 1.0) return { bg: "rgba(239, 68, 68, 0.15)", border: "#ef4444", text: "#ef4444" };
      if (sim >= 0.8) return { bg: "rgba(245, 158, 11, 0.15)", border: "#f59e0b", text: "#f59e0b" };
      if (sim >= 0.6) return { bg: "rgba(59, 130, 246, 0.15)", border: "#3b82f6", text: "#3b82f6" };
      return { bg: "rgba(0, 0, 0, 0.03)", border: "rgba(0, 0, 0, 0.1)", text: "rgba(0, 0, 0, 0.4)" };
    };

    return (
      <div className="zen-form-container">
        <div className="zen-gradient" />
        <div className="zen-results-content">
          {/* Header with back button */}
          <button onClick={handleNewSearch} className="zen-back-to-search">
            <svg width="16" height="10" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-left">
              <path
                d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="4"
              />
            </svg>
            New Search
          </button>

          {/* Hero card with name and grade */}
          <div className="zen-results-hero">
            <div
              className="zen-results-grade"
              style={{
                background: getGradeColor(adaptedData.grade).bg,
                color: getGradeColor(adaptedData.grade).text,
                borderColor: getGradeColor(adaptedData.grade).border,
              }}
            >
              {adaptedData.grade}
            </div>
            <h1 className="zen-results-name">{formData.name}</h1>
            {adaptedData.url && (
              <a href={adaptedData.url} target="_blank" rel="noopener noreferrer" className="zen-results-link">
                View on TMView →
              </a>
            )}
          </div>

          {/* Stats row */}
          <div className="zen-results-stats">
            <div className="zen-stat">
              <span className="zen-stat-value">{adaptedData.count}</span>
              <span className="zen-stat-label">results</span>
            </div>
            <div className="zen-stat">
              <span className="zen-stat-value">{exactMatches.length}</span>
              <span className="zen-stat-label">exact</span>
            </div>
            <div className="zen-stat">
              <span className="zen-stat-value">{highSimilarityMatches.length}</span>
              <span className="zen-stat-label">high sim</span>
            </div>
          </div>

          {/* Decision flow */}
          <div className="zen-decision-flow">
            <MermaidDiagram data={adaptedData} query={formData.name} />
          </div>

          {/* Conflicts as tiles */}
          {adaptedData.sample_results?.length > 0 && (
            <div className="zen-conflicts">
              <h3 className="zen-conflicts-title">
                Potential Conflicts
                <span className="zen-conflicts-count">{adaptedData.sample_results.length}</span>
              </h3>
              <div className="zen-conflicts-grid">
                {adaptedData.sample_results.map((result, idx) => {
                  const simColor = getSimilarityColor(result.similarity_to_query || 0);
                  return (
                    <a
                      key={idx}
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="zen-conflict-tile"
                      style={{ borderColor: simColor.border }}
                    >
                      {/* Similarity badge */}
                      <div
                        className="zen-conflict-sim"
                        style={{
                          background: simColor.bg,
                          color: simColor.text,
                        }}
                      >
                        {result.similarity_to_query ? `${(result.similarity_to_query * 100).toFixed(0)}%` : "—"}
                      </div>

                      {/* Name */}
                      <span className="zen-conflict-name">{result.name}</span>

                      {/* Meta */}
                      <div className="zen-conflict-meta">
                        <span className="zen-conflict-country">{result.country_code}</span>
                        {result.all_classes?.length > 0 && (
                          <span className="zen-conflict-classes">
                            {result.all_classes.slice(0, 3).join(", ")}
                            {result.all_classes.length > 3 && ` +${result.all_classes.length - 3}`}
                          </span>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {(!adaptedData.sample_results || adaptedData.sample_results.length === 0) && (
            <div className="zen-no-conflicts">No conflicts found</div>
          )}
        </div>
        <style>{zenStyles}</style>
      </div>
    );
  }

  // Show loading state when searching from history
  if (loading && !showResults) {
    return (
      <div className="zen-form-container">
        <div className="zen-gradient" />
        <div className="zen-content">
          <div className="zen-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="zen-loading-icon" />
            <p className="zen-loading-text">Searching trademarks...</p>
          </div>
        </div>
        <style>{zenStyles}</style>
      </div>
    );
  }

  return (
    <div className="zen-form-container">
      {/* Animated gradient overlay */}
      <div className="zen-gradient" />

      <div className="zen-content">
        {/* Progress */}
        <div className="zen-progress">
          {STEPS.map((_, i) => (
            <div key={i} className={`zen-dot ${i <= step ? "active" : ""}`} />
          ))}
        </div>

        {/* Question */}
        <h2 className="zen-question" key={step}>
          {currentStep.prompt}
        </h2>

        {/* Input Area */}
        <div className="zen-input-area">
          {/* Text input for name */}
          {currentStep.key === "name" && (
            <>
              <input
                ref={inputRef}
                type="text"
                value={formData.name || ""}
                onChange={e => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={currentStep.placeholder}
                className="zen-input"
              />

              {/* History section */}
              {currentStep.showHistory && history.length > 0 && (
                <div className="zen-history">
                  <button className="zen-history-toggle" onClick={() => setShowHistory(!showHistory)}>
                    <span>Your recent searches</span>
                    <FontAwesomeIcon icon={showHistory ? faChevronUp : faChevronDown} />
                  </button>

                  {showHistory && (
                    <div className="zen-history-grid">
                      {history.map(item => {
                        const gradeColor = getGradeColor(item.grade);
                        const searchDate = new Date(item.searchedAt);
                        const isToday = new Date().toDateString() === searchDate.toDateString();
                        const dateStr = isToday
                          ? searchDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : searchDate.toLocaleDateString([], { month: "short", day: "numeric" });
                        return (
                          <button key={item.id} className="zen-history-tile" onClick={() => loadFromHistory(item)}>
                            {/* Grade badge */}
                            <div
                              className="zen-history-tile-grade"
                              style={{
                                background: gradeColor.bg,
                                color: gradeColor.text,
                                borderColor: gradeColor.border,
                              }}
                            >
                              {item.grade}
                            </div>

                            {/* Name */}
                            <span className="zen-history-tile-name">{item.query}</span>

                            {/* Meta info */}
                            <div className="zen-history-tile-meta">
                              {item.countries?.length > 0 && (
                                <span className="zen-history-tile-countries">
                                  {item.countries.slice(0, 2).join(", ")}
                                  {item.countries.length > 2 && ` +${item.countries.length - 2}`}
                                </span>
                              )}
                              {item.classes?.length > 0 && (
                                <span className="zen-history-tile-classes">
                                  Class {item.classes.slice(0, 2).join(", ")}
                                  {item.classes.length > 2 && ` +${item.classes.length - 2}`}
                                </span>
                              )}
                            </div>

                            {/* Date */}
                            <span className="zen-history-tile-date">{dateStr}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Tile Grid for Countries */}
          {currentStep.tiles === "countries" && (
            <div className="zen-tiles">
              <input
                type="text"
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
                placeholder="Search countries..."
                className="zen-tiles-search-input"
              />

              <div className="zen-tiles-grid zen-tiles-grid-countries">
                {(countrySearch
                  ? ALL_COUNTRIES.filter(
                      c =>
                        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                        c.code.toLowerCase().includes(countrySearch.toLowerCase())
                    )
                  : showAllCountries
                    ? [...POPULAR_COUNTRIES, ...ALL_COUNTRIES.filter(c => !c.popular)]
                    : POPULAR_COUNTRIES
                ).map(item => {
                  const isSelected = (formData.countries || []).includes(item.code);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      className={`zen-tile ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        const current = formData.countries || [];
                        const updated = isSelected ? current.filter(c => c !== item.code) : [...current, item.code];
                        setFormData({ ...formData, countries: updated });
                      }}
                      title={item.name}
                    >
                      <span className="zen-tile-flag">{item.flag}</span>
                      <span className="zen-tile-code">{item.code}</span>
                    </button>
                  );
                })}
              </div>

              {!countrySearch && (
                <button
                  type="button"
                  className="zen-tiles-toggle"
                  onClick={() => setShowAllCountries(!showAllCountries)}
                >
                  {showAllCountries ? "− Show less" : `+ ${ALL_COUNTRIES.length - POPULAR_COUNTRIES.length} more`}
                </button>
              )}

              {/* Selected items as removable chips */}
              {(formData.countries || []).length > 0 && (
                <div className="zen-selected-chips">
                  {(formData.countries || []).map(code => {
                    const country = ALL_COUNTRIES.find(c => c.code === code);
                    return (
                      <button
                        key={code}
                        type="button"
                        className="zen-selected-chip"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            countries: (formData.countries || []).filter(c => c !== code),
                          });
                        }}
                      >
                        {country?.flag} {code}
                        <span className="zen-chip-x">×</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tile Grid for Nice Classes */}
          {currentStep.tiles === "classes" && (
            <div className="zen-tiles">
              <input
                type="text"
                value={classSearch}
                onChange={e => setClassSearch(e.target.value)}
                placeholder="Search classes..."
                className="zen-tiles-search-input"
              />

              <div className="zen-tiles-grid zen-tiles-grid-classes">
                {(classSearch
                  ? ALL_NICE_CLASSES.filter(
                      c =>
                        c.name.toLowerCase().includes(classSearch.toLowerCase()) ||
                        c.code.toString().includes(classSearch)
                    )
                  : showAllClasses
                    ? [...POPULAR_CLASSES, ...ALL_NICE_CLASSES.filter(c => !c.popular)]
                    : POPULAR_CLASSES
                ).map(item => {
                  const isSelected = (formData.classes || []).includes(item.code);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      className={`zen-tile zen-tile-class ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        const current = formData.classes || [];
                        const updated = isSelected ? current.filter(c => c !== item.code) : [...current, item.code];
                        setFormData({ ...formData, classes: updated });
                      }}
                    >
                      <span className="zen-tile-class-num">{item.code}</span>
                      <span className="zen-tile-class-name">{item.name}</span>
                    </button>
                  );
                })}
              </div>

              {!classSearch && (
                <button type="button" className="zen-tiles-toggle" onClick={() => setShowAllClasses(!showAllClasses)}>
                  {showAllClasses ? "− Show less" : `+ ${ALL_NICE_CLASSES.length - POPULAR_CLASSES.length} more`}
                </button>
              )}

              {/* Selected items as removable chips */}
              {(formData.classes || []).length > 0 && (
                <div className="zen-selected-chips">
                  {(formData.classes || []).map(code => {
                    const cls = ALL_NICE_CLASSES.find(c => c.code === code);
                    return (
                      <button
                        key={code}
                        type="button"
                        className="zen-selected-chip"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            classes: (formData.classes || []).filter(c => c !== code),
                          });
                        }}
                      >
                        {code}: {cls?.name}
                        <span className="zen-chip-x">×</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Review Screen */}
          {currentStep.review && (
            <div className="zen-review">
              <div className="zen-review-grid">
                <div className="zen-review-item zen-review-item-full">
                  <span className="zen-review-label">Brand Name</span>
                  <span className="zen-review-value zen-review-value-name">{formData.name || "—"}</span>
                </div>
              </div>

              {(formData.countries || []).length > 0 && (
                <div className="zen-review-block">
                  <span className="zen-review-label">Countries ({formData.countries.length})</span>
                  <div className="zen-review-tags">
                    {formData.countries.map(code => {
                      const country = ALL_COUNTRIES.find(c => c.code === code);
                      return (
                        <span key={code} className="zen-review-tag">
                          {country?.flag} {code}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {(formData.classes || []).length > 0 && (
                <div className="zen-review-block">
                  <span className="zen-review-label">Classes ({formData.classes.length})</span>
                  <div className="zen-review-tags">
                    {formData.classes.map(code => (
                      <span key={code} className="zen-review-tag">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Search type selector */}
              <div className="zen-search-options">
                <span className="zen-review-label">Match Type</span>
                <div className="zen-search-options-grid">
                  {[
                    { value: "contains", label: "Contains", desc: "Name appears anywhere" },
                    { value: "exact", label: "Exact", desc: "Exact match only" },
                    { value: "fuzzy", label: "Fuzzy", desc: "Similar spellings" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`zen-search-option ${formData.searchType === opt.value ? "selected" : ""}`}
                      onClick={() => setFormData({ ...formData, searchType: opt.value })}
                    >
                      <span className="zen-search-option-label">{opt.label}</span>
                      <span className="zen-search-option-desc">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && <div className="zen-error">{error}</div>}

        {/* Navigation */}
        <div className="zen-nav">
          {step === 0 ? (
            <div className="relative">
              <button onClick={() => navigate('/')} className="zen-nav-btn zen-back">
                <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-left">
                  <path
                    d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                </svg>
              </button>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-[10px] text-base-content/30">Exit</span>
            </div>
          ) : (
            <button onClick={handleBack} className="zen-nav-btn zen-back">
              <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-left">
                <path
                  d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="4"
                />
              </svg>
            </button>
          )}
          <div className="zen-nav-spacer" />
          <button onClick={handleNext} disabled={!canProceed() || loading} className="zen-nav-btn zen-next">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : isLastStep ? (
              <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-down">
                <path
                  d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="4"
                />
              </svg>
            ) : (
              <svg width="24" height="14" viewBox="0 0 81.153 43.501" className="zen-swoosh-icon zen-swoosh-right">
                <path
                  d="M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="4"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      <style>{zenStyles}</style>
    </div>
  );
}

export default Scrapers;